import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Button,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteIcon from "@material-ui/icons/Delete";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import AddIcon from "@material-ui/icons/Add";
import EventTwoToneIcon from "@material-ui/icons/EventTwoTone";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import axios from "axios";
import { useHistory, useParams } from "react-router-dom";
import { appUrl } from "../../../../../../../utils/config";
import { useStyles } from "./ProductDetailsStyles";
import { updateProjectDetailsAPI } from "../stepperAPI";
import jwt_decode from "jwt-decode";

// Static data
const PRODUCT_MODALITIES = [
  "Small Molecules",
  "Large Molecules",
  "Biologics",
  "Vaccines",
];

const PRODUCT_TYPES = ["API", "Drug Product", "Intermediate", "Raw Material"];

const MANUFACTURING_FACILITY_TYPES = [
  "Formulation & Packaging (Drug Product) - Oral Solid Dosage",
  "Formulation & Packaging (Drug Product) - Injectable",
  "API Manufacturing",
  "Packaging Only",
];

const MANUFACTURING_SUPPLY_TYPES = [
  "Commercial Supply",
  "Clinical Supply",
  "R&D Supply",
];

const BRANDS = ["Baraclude", "Sprycel", "Iclusig", "Bosulif"];

const UOM_OPTIONS = ["KG", "G", "MG", "L", "ML", "Units"];

const STATUS_OPTIONS = ["Active", "Inactive", "Pending"];

const MATERIAL_TYPES = [
  "Bulk Drug Product Bulk DP",
  "Raw Material",
  "Intermediate",
  "API",
];

const PLANT_IDS = ["1007", "1008", "1009"];
const STORAGE_LOCATION_IDS = ["0002", "0005", "0006", "0008"];

// Helper function to generate unique material ID
function generateMaterialId() {
  return `material_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}


const ProductDetails = ({
  formData,
  updateStepData,
  classes: parentClasses,
  handleNext,
  handleBack,
  activeStep,
  stepsLength,
  markApiCallSuccessful,
  hideStepActions = false, // Hide navigation buttons when used in review page
  readOnly = false, // Read-only mode for review page
  isEditMode = false, // Edit Project / Edit Campaign: Deliverable Type is not shown
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { roleName, organizationName, ecosystemId } = useParams();
  
  // Initialize step data from formData or use defaults
  const [stepData, setStepData] = useState(() => {
    const saved = formData?.productDetails || {};
    return {
      selectedProduct: saved.selectedProduct || "", // Product Code / Description
      projectID: saved.projectID || "",
      cmoSite: saved.cmoSite || "",
      deliverableType: saved.deliverableType ? (Array.isArray(saved.deliverableType) ? saved.deliverableType : [saved.deliverableType]) : [],
      projectStartDate: saved.projectStartDate || null,
      projectEndDate: saved.projectEndDate || null,
      productModality: saved.productModality || "NA",
      manufacturingFacilityType: saved.manufacturingFacilityType || "NA",
      productTypeName: saved.productTypeName || "NA",
      manufacturingSupplyType: saved.manufacturingSupplyType || "NA",
      ...saved, // Keep any other existing fields
    };
  });

  // API base URL - must be declared before useEffect that uses it
  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");

    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, []);

  // Helper to build product label for dropdown - must be declared before useEffect that uses it
  const getProductLabel = useCallback((product) => {
    if (!product) return "";
    const code = product.productCode || "";
    const desc = product.productDescription || "";
    if (code && desc) return `${code} / ${desc}`;
    return code || desc || "";
  }, []);

  // Sync stepData when formData.productDetails changes (for edit mode when API data loads)
  useEffect(() => {
    if (formData?.productDetails && Object.keys(formData.productDetails).length > 0) {
      const saved = formData.productDetails;
      console.log("🔄 ProductDetails: Syncing stepData from formData:", saved);
      
      setStepData(prev => {
        const updated = {
          ...prev,
          selectedProduct: saved.selectedProduct !== undefined ? saved.selectedProduct : prev.selectedProduct,
          projectID: saved.projectID !== undefined ? saved.projectID : prev.projectID,
          cmoSite: saved.cmoSite !== undefined ? saved.cmoSite : prev.cmoSite,
          deliverableType: saved.deliverableType 
            ? (Array.isArray(saved.deliverableType) ? saved.deliverableType : [saved.deliverableType])
            : prev.deliverableType,
          projectStartDate: saved.projectStartDate !== undefined ? saved.projectStartDate : prev.projectStartDate,
          projectEndDate: saved.projectEndDate !== undefined ? saved.projectEndDate : prev.projectEndDate,
          collabrixProductID: saved.collabrixProductID !== undefined ? saved.collabrixProductID : prev.collabrixProductID,
          collabrixProjectID: saved.collabrixProjectID !== undefined ? saved.collabrixProjectID : prev.collabrixProjectID,
          sponsorOrgID: saved.sponsorOrgID !== undefined ? saved.sponsorOrgID : prev.sponsorOrgID,
          cdmoOrgID: saved.cdmoOrgID !== undefined ? saved.cdmoOrgID : prev.cdmoOrgID,
          deliverableTypeIDs: saved.deliverableTypeIDs !== undefined ? saved.deliverableTypeIDs : prev.deliverableTypeIDs,
          // Product attributes (read-only display) must sync from API data too,
          // otherwise they stay "NA" in edit mode.
          productModality: saved.productModality !== undefined ? saved.productModality : prev.productModality,
          productTypeName: saved.productTypeName !== undefined ? saved.productTypeName : prev.productTypeName,
          manufacturingFacilityType: saved.manufacturingFacilityType !== undefined ? saved.manufacturingFacilityType : prev.manufacturingFacilityType,
          manufacturingSupplyType: saved.manufacturingSupplyType !== undefined ? saved.manufacturingSupplyType : prev.manufacturingSupplyType,
        };
        
        console.log("🔄 ProductDetails: Updated stepData:", updated);
        return updated;
      });
      
      // Note: selectedCmoVendorId restoration is handled in a separate useEffect below
      // that runs after cmoSites is loaded to avoid initialization issues
      
      // If collabrixProductID and cdmoOrgID are set, fetch product details to get product name/description
      if (saved.collabrixProductID && saved.cdmoOrgID) {
        // Fetch products for the CMO to get product details
        const fetchProductDetails = async () => {
          try {
            console.log("🔄 ProductDetails: Fetching product details for collabrixProductID:", saved.collabrixProductID, "cdmoOrgID:", saved.cdmoOrgID);
            const response = await axios.get(`${apiBaseUrl}/fetchAllProductsForCMO/${saved.cdmoOrgID}`);
            if (response?.data?.status === true || response?.data?.status === "true") {
              const products = response.data?.data?.productDetailsList || response.data?.data?.productsList || [];
              console.log("🔄 ProductDetails: Fetched products:", products);
              const matchedProduct = products.find(p => p.collabrixProductID === saved.collabrixProductID);
              if (matchedProduct) {
                console.log("🔄 ProductDetails: Matched product:", matchedProduct);
                // Update selectedProduct with the product label (code / description)
                const productLabel = getProductLabel(matchedProduct);
                setStepData(prev => ({
                  ...prev,
                  selectedProduct: productLabel,
                }));
              } else {
                console.warn("🔄 ProductDetails: No product found with collabrixProductID:", saved.collabrixProductID);
              }
            }
          } catch (error) {
            console.error("Error fetching product details:", error);
          }
        };
        fetchProductDetails();
      }
    }
  }, [formData?.productDetails, apiBaseUrl, getProductLabel]);


  // Generate a shared default material ID for linking sponsor and CMO rows
  const defaultMaterialIdRef = useRef(generateMaterialId());

  // Material Details (Sponsor) state - Start with 1 empty row by default
  const [materialDetailsList, setMaterialDetailsList] = useState(() => {
    const saved = formData?.productDetails?.materialDetailsList || [];
    if (saved.length > 0) {
      return saved;
    }
    // Default: 1 empty row for first time
    return [
      {
        _materialId: defaultMaterialIdRef.current,
        sponsorSupplyingMaterial: true,
        material_brand: "",
        materialTypeName: "",
        material_type_id: "",
        bo_material_code: "",
        material_description: "",
        bo_material_UOM: "",
        materialPlantIDFromSponsorERP: "",
        materialStorageLocationIDsFromSponsorERP: "",
        cmo_product_code: "",
        cmo_material_code: "",
        cmo_material_description: "",
        cmo_material_UOM: "",
      },
    ];
  });

  // Material Details (CMO) state - Start with 1 empty row linked to sponsor row
  const [cmoMaterialDetailsList, setCmoMaterialDetailsList] = useState(() => {
    const saved = formData?.productDetails?.cmoMaterialDetailsList || [];
    if (saved.length > 0) {
      return saved;
    }
    // Default: 1 empty row linked to the sponsor row (same material ID)
    // Use the ref to ensure same ID as sponsor row
    const sponsorMaterialId = defaultMaterialIdRef.current;
    return [
      {
        _materialId: sponsorMaterialId,
        id: sponsorMaterialId,
        cmo_product_code: "",
        bo_material_code: "",
        material_description: "",
        cmo_material_code: "",
        cmo_material_description: "",
        cmo_material_UOM: "",
      },
    ];
  });

  // Track initial row counts to identify existing vs newly added rows
  const initialMaterialDetailsCount = useMemo(() => {
    const saved = formData?.productDetails?.materialDetailsList || [];
    return saved.length > 0 ? saved.length : 1; // Changed from 7 to 1
  }, []);

  const initialCmoMaterialDetailsCount = useMemo(() => {
    const saved = formData?.productDetails?.cmoMaterialDetailsList || [];
    return saved.length > 0 ? saved.length : 1;
  }, []);

  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(
    formData?.productDetails?.uploadedFileName || ""
  );
  
  // Brand dialog state
  const [openBrandDialog, setOpenBrandDialog] = useState(false);
  const [newBrand, setNewBrand] = useState("");
  const [brandError, setBrandError] = useState("");
  const [brands, setBrands] = useState(BRANDS);
  
  // API data states - store data fetched from backend
  const [productModalities, setProductModalities] = useState([]);
  const [facilityTypes, setFacilityTypes] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [vendorPlants, setVendorPlants] = useState([]);
  const [cmoSites, setCmoSites] = useState([]); // [{ vendorMosymphonyID, siteNameFromReport }]
  const [selectedCmoVendorId, setSelectedCmoVendorId] = useState(null);
  const [cmoProducts, setCmoProducts] = useState([]); // Products for selected CMO
  const [deliverableTypes, setDeliverableTypes] = useState([]); // Deliverable types from API
  const [uomOptions, setUomOptions] = useState([]); // UOM options from API
  
  // Loading states for tracking API fetch status
  const [isLoading, setIsLoading] = useState({
    productModalities: false,
    facilityTypes: false,
    productTypes: false,
    brands: false,
    vendorPlants: false,
    cmoSites: false,
    cmoProducts: false,
    deliverableTypes: false,
    uomOptions: false,
  });
  
  // Error handling states
  const [apiErrors, setApiErrors] = useState({});
  
  // Ref to track if APIs have been loaded
  const dropdownAPIsLoadedRef = useRef(false);
  
  // Note: apiBaseUrl and getProductLabel are declared earlier (before the useEffect that uses them)

  // Fetch deliverable types from API
  useEffect(() => {
    const fetchDeliverableTypes = async () => {
      if (!apiBaseUrl || readOnly) {
        return;
      }
      try {
        setIsLoading((prev) => ({ ...prev, deliverableTypes: true }));
        const response = await axios.get(`${apiBaseUrl}/fetchDeliverableTypes/true`);
        if (response?.data?.status === true || response?.data?.status === "true") {
          const list = response?.data?.data?.deliverablesTypes || [];
          // Only include activated deliverable types
          const activatedTypes = list.filter(type => type.activated === true);
          setDeliverableTypes(activatedTypes);
        } else {
          setDeliverableTypes([]);
        }
      } catch (error) {
        console.error("Error fetching deliverable types:", error);
        setDeliverableTypes([]);
      } finally {
        setIsLoading((prev) => ({ ...prev, deliverableTypes: false }));
      }
    };

    fetchDeliverableTypes();
  }, [apiBaseUrl, readOnly]);

  // Fetch UOM options from API
  useEffect(() => {
    const fetchUOMs = async () => {
      if (!apiBaseUrl || readOnly) {
        return;
      }
      try {
        setIsLoading((prev) => ({ ...prev, uomOptions: true }));
        const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchAllUOMs/false`;
        const response = await axios.get(url);
        const data = response?.data?.data;
        
        if (data?.status === true || data?.status === "true") {
          const list = data.productUOMsList || [];
          // Extract UOM strings from activated items
          const uomList = list
            .filter(item => item.activated === true || item.activated === "true")
            .map(item => item.uom)
            .filter(uom => uom); // Remove any empty/null values
          setUomOptions(uomList);
        } else {
          setUomOptions([]);
        }
      } catch (error) {
        console.error("Error fetching UOM options:", error);
        setUomOptions([]);
      } finally {
        setIsLoading((prev) => ({ ...prev, uomOptions: false }));
      }
    };

    fetchUOMs();
  }, [apiBaseUrl, readOnly]);

  // Helper function to convert deliverableType array to comma-separated IDs string using dynamic mapping
  const formatDeliverableTypeToIDs = useCallback((deliverableTypeArray) => {
    if (!deliverableTypeArray || !Array.isArray(deliverableTypeArray) || deliverableTypeArray.length === 0) {
      return "";
    }
    
    // Create dynamic mapping from fetched deliverable types
    const deliverableTypeMap = {};
    deliverableTypes.forEach(type => {
      if (type.deliverableTypeName && type.deliverableTypeID) {
        deliverableTypeMap[type.deliverableTypeName] = type.deliverableTypeID;
      }
    });
    
    const ids = deliverableTypeArray
      .map(type => deliverableTypeMap[type])
      .filter(id => id != null && id !== undefined);
    
    return ids.join(",");
  }, [deliverableTypes]);

  // Helper to get cdmoOrgID from CMO site selection
  const getCdmoOrgID = useCallback(() => {
    try {
      if (stepData.cmoSite && cmoSites.length > 0) {
        const matched = cmoSites.find(
          (site) => site?.siteNameFromReport && 
          site.siteNameFromReport.toLowerCase() === String(stepData.cmoSite).toLowerCase()
        );
        if (matched?.vendorMosymphonyID) {
          return matched.vendorMosymphonyID;
        }
      }
      // Try to get from sessionStorage
      const savedFormData = sessionStorage.getItem("productDevelopmentFormData");
      if (savedFormData) {
        const parsed = JSON.parse(savedFormData);
        return parsed.productDetails?.cdmoOrgID || null;
      }
    } catch (error) {
      console.error("Error getting cdmoOrgID:", error);
    }
    return null;
  }, [stepData.cmoSite, cmoSites]);

  // Helper to get sponsorOrgID from token or formData (same pattern as other components)
  const getSponsorOrgID = useCallback(() => {
    try {
      const token = localStorage.getItem("authToken");
      if (token) {
        const decoded = jwt_decode(token);
        // Use organization_id (with underscore) - same as AddCampaignID and other components
        return decoded?.organization_id || null;
      }
      // Fallback: try to get from sessionStorage
      const savedFormData = sessionStorage.getItem("productDevelopmentFormData");
      if (savedFormData) {
        const parsed = JSON.parse(savedFormData);
        return parsed.productDetails?.sponsorOrgID || null;
      }
    } catch (error) {
      console.error("Error getting sponsorOrgID:", error);
    }
    return null;
  }, []);

  // Helper to get collabrixProjectID from formData
  const getCollabrixProjectID = useCallback(() => {
    try {
      const savedFormData = sessionStorage.getItem("productDevelopmentFormData");
      if (savedFormData) {
        const parsed = JSON.parse(savedFormData);
        return parsed.productDetails?.collabrixProjectID || null;
      }
    } catch (error) {
      console.error("Error getting collabrixProjectID:", error);
    }
    return null;
  }, []);

  // Fetch CMO sites list for CMO - Site dropdown
  useEffect(() => {
    const fetchCmoSites = async () => {
      if (!apiBaseUrl || readOnly) {
        return;
      }
      try {
        setIsLoading((prev) => ({ ...prev, cmoSites: true }));
        const response = await axios.get(`${apiBaseUrl}/fetchAllCMOSites`);
        if (response?.data?.status === true || response?.data?.status === "true") {
          const list = response?.data?.data?.cmoSitesList || [];
          setCmoSites(list);

          // If stepData already has a cmoSite (edit mode), try to map it to vendorMosymphonyID
          // cmoSite might be the vendorMosymphonyID (number) or site name (string)
          if (stepData.cmoSite && !selectedCmoVendorId) {
            // First try to match by vendorMosymphonyID (if cmoSite is a number/ID)
            let matched = list.find(
              (item) => String(item?.vendorMosymphonyID) === String(stepData.cmoSite)
            );
            
            // If not found, try to match by site name
            if (!matched) {
              matched = list.find(
                (item) =>
                  item?.siteNameFromReport &&
                  item.siteNameFromReport.toLowerCase() === String(stepData.cmoSite).toLowerCase()
              );
            }
            
            if (matched?.vendorMosymphonyID !== undefined && matched.vendorMosymphonyID !== null) {
              setSelectedCmoVendorId(String(matched.vendorMosymphonyID));
              // Also fetch products for this CMO
              fetchProductsForCmo(String(matched.vendorMosymphonyID));
            }
          }
        } else {
          setCmoSites([]);
        }
      } catch (error) {
        console.error("Error fetching CMO sites for Product Details:", error);
        setCmoSites([]);
      } finally {
        setIsLoading((prev) => ({ ...prev, cmoSites: false }));
      }
    };

    fetchCmoSites();
    // We intentionally don't include stepData / selectedCmoVendorId here to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, readOnly]);

  // Fetch products for selected CMO (fetchAllProductsForCMO)
  const fetchProductsForCmo = useCallback(
    async (vendorMosymphonyID) => {
      if (!apiBaseUrl || !vendorMosymphonyID || readOnly) {
        return;
      }
      try {
        setIsLoading((prev) => ({ ...prev, cmoProducts: true }));
        const url = `${apiBaseUrl}/fetchAllProductsForCMO/${vendorMosymphonyID}`;
        const response = await axios.get(url);
        if (response?.data?.status === true || response?.data?.status === "true") {
          const list = response?.data?.data?.productDetailsList || [];
          setCmoProducts(list);
        } else {
          setCmoProducts([]);
        }
      } catch (error) {
        console.error("Error fetching products for CMO:", error);
        setCmoProducts([]);
      } finally {
        setIsLoading((prev) => ({ ...prev, cmoProducts: false }));
      }
    },
    [apiBaseUrl, readOnly]
  );

  // Separate useEffect to restore selectedCmoVendorId when cmoSites is loaded and stepData.cmoSite is set
  // This runs after cmoSites and fetchProductsForCmo are initialized to avoid "Cannot access before initialization" error
  useEffect(() => {
    if (stepData.cmoSite && cmoSites.length > 0 && !selectedCmoVendorId) {
      // Try to find the site by siteNameFromReport first (more specific)
      let matched = cmoSites.find(
        (site) =>
          site?.siteNameFromReport &&
          site.siteNameFromReport.toLowerCase() === String(stepData.cmoSite).toLowerCase()
      );
      
      // If not found, try to match by vendorMosymphonyID (if cmoSite is a number/ID)
      if (!matched) {
        matched = cmoSites.find(
          (site) => String(site?.vendorMosymphonyID) === String(stepData.cmoSite)
        );
      }
      
      if (matched?.vendorMosymphonyID !== undefined && matched.vendorMosymphonyID !== null) {
        setSelectedCmoVendorId(String(matched.vendorMosymphonyID));
        // Also fetch products for this CMO
        fetchProductsForCmo(String(matched.vendorMosymphonyID));
      }
    }
  }, [stepData.cmoSite, cmoSites, selectedCmoVendorId, fetchProductsForCmo]);

  // Re-validate persisted selectedProduct against the freshly fetched product list.
  // Without this, a snapshot from an earlier session can leave the four auto-populate
  // fields (modality / facility / type / supply) showing values that no longer match
  // any product under the current CMO — the dropdown reads "Select Product" while
  // stale labels persist below it.
  useEffect(() => {
    if (isLoading.cmoProducts) return;
    if (!cmoProducts || cmoProducts.length === 0) return;
    if (!stepData.selectedProduct) return;

    const matched = cmoProducts.find(
      (p) => getProductLabel(p) === stepData.selectedProduct
    );

    if (matched) {
      setStepData((prev) => {
        const next = {
          productModality: matched.drugTypeName || "NA",
          manufacturingFacilityType: matched.facilityTypeName || "NA",
          productTypeName: matched.productTypeName || "NA",
          manufacturingSupplyType: matched.supplyTypeName || "NA",
          collabrixProductID: matched.collabrixProductID,
        };
        if (
          prev.productModality === next.productModality &&
          prev.manufacturingFacilityType === next.manufacturingFacilityType &&
          prev.productTypeName === next.productTypeName &&
          prev.manufacturingSupplyType === next.manufacturingSupplyType &&
          prev.collabrixProductID === next.collabrixProductID
        ) {
          return prev;
        }
        return { ...prev, ...next };
      });
    } else {
      setStepData((prev) => {
        if (
          !prev.selectedProduct &&
          prev.productModality === "NA" &&
          prev.manufacturingFacilityType === "NA" &&
          prev.productTypeName === "NA" &&
          prev.manufacturingSupplyType === "NA"
        ) {
          return prev;
        }
        return {
          ...prev,
          selectedProduct: "",
          productModality: "NA",
          manufacturingFacilityType: "NA",
          productTypeName: "NA",
          manufacturingSupplyType: "NA",
          collabrixProductID: undefined,
        };
      });
    }
  }, [cmoProducts, isLoading.cmoProducts, stepData.selectedProduct, getProductLabel]);

  // REMOVED: fetchVendorPlantsData - Not needed as vendorPlants are only used in material details table
  // which is not sent to updateProjectDetails API

  // REMOVED: fetchProductModalities, fetchFacilityTypes, fetchProductTypes, fetchBrands
  // These are only used for read-only display (auto-populated from product selection)
  // and are NOT sent to updateProjectDetails API

  // REMOVED: fetchPlantIDStorageID, plantIDStorageID state, receivingStorageLocationIDs, and receivingPlantIDs
  // These were unused static data. Static arrays PLANT_IDS and STORAGE_LOCATION_IDS are used directly in the UI

  // Memoize validation function
  const validate = useCallback(() => {
    // COMMENTED OUT FOR TESTING - Always return true to allow navigation through all steppers
    // const newErrors = {};

    // if (!stepData.productCode?.trim()) {
    //   newErrors.productCode = "Product Code is required";
    // }

    // if (!stepData.projectID?.trim()) {
    //   newErrors.projectID = "Project ID is required";
    // }

    // if (!stepData.productDescription?.trim()) {
    //   newErrors.productDescription = "Product Description is required";
    // }

    // if (!stepData.cmoProductCode?.trim()) {
    //   newErrors.cmoProductCode = "CMO Product Code is required";
    // }

    // if (!stepData.cmoProductDescription?.trim()) {
    //   newErrors.cmoProductDescription = "CMO Product Description is required";
    // }

    // if (!stepData.brand) {
    //   newErrors.brand = "Brand is required";
    // }

    // if (!stepData.sponsorUOM) {
    //   newErrors.sponsorUOM = "Sponsor UOM is required";
    // }

    // if (!stepData.cmoUOM) {
    //   newErrors.cmoUOM = "CMO UOM is required";
    // }

    // setErrors(newErrors);
    // return Object.keys(newErrors).length === 0;
    return true; // Always return true for testing
  }, [stepData]);

  // Optimized field change handler with debounced updateStepData
  const handleFieldChange = useCallback(
    (name, value) => {
      const updatedStepData = {
        ...stepData,
        [name]: value,
      };

      // Special case handlers for fields that require additional data processing
      if (name === "productModality" && value) {
        const selectedModality = productModalities?.find(
          (modality) => modality.drugTypeName === value
        );
        if (selectedModality) {
          updatedStepData.drugTypeID = selectedModality.drugTypeID;
        }
      }

      if (name === "productTypeName" && value) {
        const selectedProductType = productTypes?.find(
          (product) => product.productTypeName === value
        );
        if (selectedProductType) {
          updatedStepData.productTypeID = selectedProductType.productTypeID;
        }
      }

      if (name === "manufacturingFacilityType" && value) {
        const selectedFacilityType = facilityTypes?.find(
          (facility) => facility.facilityTypeName === value
        );
        if (selectedFacilityType) {
          updatedStepData.facilityTypeID = selectedFacilityType.facilityTypeID;
        }
      }

      // Reset product and related fields when CMO Site changes
      if (name === "cmoSite") {
        updatedStepData.selectedProduct = "";
        updatedStepData.productModality = "NA";
        updatedStepData.manufacturingFacilityType = "NA";
        updatedStepData.productTypeName = "NA";
        updatedStepData.manufacturingSupplyType = "NA";
      }

      // Auto-populate product details when product is selected - using products from fetchAllProductsForCMO
      if (name === "selectedProduct" && value) {
        const selectedProduct = cmoProducts.find(
          (product) => getProductLabel(product) === value
        );

        if (selectedProduct) {
          updatedStepData.productModality =
            selectedProduct.drugTypeName || updatedStepData.productModality || "NA";
          updatedStepData.manufacturingFacilityType =
            selectedProduct.facilityTypeName || updatedStepData.manufacturingFacilityType || "NA";
          updatedStepData.productTypeName =
            selectedProduct.productTypeName || updatedStepData.productTypeName || "NA";
          updatedStepData.manufacturingSupplyType =
            selectedProduct.supplyTypeName || updatedStepData.manufacturingSupplyType || "NA";
          updatedStepData.collabrixProductID = selectedProduct.collabrixProductID;
        }
      }

      // Reset to NA when product is deselected
      if (name === "selectedProduct" && !value) {
        updatedStepData.productModality = "NA";
        updatedStepData.manufacturingFacilityType = "NA";
        updatedStepData.productTypeName = "NA";
        updatedStepData.manufacturingSupplyType = "NA";
      }

      // When deliverable types change, keep deliverableTypeIDs in sync so edit flow sends correct payload
      if (name === "deliverableType") {
        if (Array.isArray(value) && value.length > 0) {
          const deliverableTypeMap = {};
          deliverableTypes.forEach((type) => {
            if (type.deliverableTypeName && type.deliverableTypeID) {
              deliverableTypeMap[type.deliverableTypeName] = type.deliverableTypeID;
            }
          });
          const ids = value
            .map((typeName) => deliverableTypeMap[typeName])
            .filter((id) => id != null && id !== undefined);
          updatedStepData.deliverableTypeIDs = ids.join(",");
        } else {
          updatedStepData.deliverableTypeIDs = "";
        }
      }

      // Special case for cmoPlantId composite value (if used)
      if (name === "cmoPlantId" && value) {
        const [vendorMosymphonyID, vendorPlant] = value.split("|");
        const selectedVendor = vendorPlants.find(
          (plant) =>
            String(plant?.vendorMosymphonyID) === vendorMosymphonyID &&
            plant.vendorPlant === vendorPlant
        );
        updatedStepData.vendorMosymphonyID = Number(vendorMosymphonyID);
        updatedStepData.vendorPlant = vendorPlant;
        updatedStepData.vendorName = selectedVendor
          ? selectedVendor.vendorName
          : "";
      }

      setStepData(updatedStepData);

      // Clear error for this field
      if (errors[name]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }

      // Update parent formData (debounced via updateStepData)
      updateStepData("productDetails", {
        ...updatedStepData,
        materialDetailsList,
        cmoMaterialDetailsList,
        uploadedFileName,
      });
    },
    [stepData, errors, updateStepData, materialDetailsList, cmoMaterialDetailsList, uploadedFileName, productModalities, productTypes, facilityTypes, vendorPlants, deliverableTypes]
  );

  // Handle file upload
  const handleFileUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFileName(file.name);
      updateStepData("productDetails", {
        ...stepData,
        materialDetailsList,
        cmoMaterialDetailsList,
        uploadedFileName: file.name,
      });
    }
  }, [stepData, materialDetailsList, cmoMaterialDetailsList, updateStepData]);

  // Handle material details change
  const handleMaterialDetailsChange = useCallback(
    (index, field, value) => {
      const updated = materialDetailsList.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      );
      setMaterialDetailsList(updated);
      updateStepData("productDetails", {
        ...stepData,
        materialDetailsList: updated,
        cmoMaterialDetailsList,
        uploadedFileName,
      });
    },
    [materialDetailsList, stepData, cmoMaterialDetailsList, uploadedFileName, updateStepData]
  );

  // Handle CMO material details change
  const handleCmoMaterialDetailsChange = useCallback(
    (index, field, value) => {
      const updated = cmoMaterialDetailsList.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      );
      setCmoMaterialDetailsList(updated);
      updateStepData("productDetails", {
        ...stepData,
        materialDetailsList,
        cmoMaterialDetailsList: updated,
        uploadedFileName,
      });
    },
    [cmoMaterialDetailsList, stepData, materialDetailsList, uploadedFileName, updateStepData]
  );

  // Handle add material row
  const handleAddMaterialRow = useCallback(() => {
    const newMaterialId = generateMaterialId();
    const newRow = {
      _materialId: newMaterialId,
      sponsorSupplyingMaterial: true,
      material_brand: "",
      materialTypeName: "",
      material_type_id: "",
      bo_material_code: "",
      material_description: "",
      bo_material_UOM: "",
      materialPlantIDFromSponsorERP: "",
      materialStorageLocationIDsFromSponsorERP: "",
      cmo_product_code: "",
      cmo_material_code: "",
      cmo_material_description: "",
      cmo_material_UOM: "",
    };
    const updatedList = [...materialDetailsList, newRow];
    setMaterialDetailsList(updatedList);
    
    // Add corresponding CMO material row linked to the new sponsor row
    const newCmoRow = {
      _materialId: newMaterialId, // Same ID to link them
      id: newMaterialId,
      cmo_product_code: "",
      bo_material_code: "",
      material_description: "",
      cmo_material_code: "",
      cmo_material_description: "",
      cmo_material_UOM: "",
    };
    const updatedCmoList = [...cmoMaterialDetailsList, newCmoRow];
    setCmoMaterialDetailsList(updatedCmoList);
    
    updateStepData("productDetails", {
      ...stepData,
      materialDetailsList: updatedList,
      cmoMaterialDetailsList: updatedCmoList,
      uploadedFileName,
    });
  }, [materialDetailsList, cmoMaterialDetailsList, stepData, uploadedFileName, updateStepData]);

  // Handle delete material row
  const handleDeleteMaterialRow = useCallback(
    (index) => {
      const updated = materialDetailsList.filter((_, i) => i !== index);
      setMaterialDetailsList(updated);
      updateStepData("productDetails", {
        ...stepData,
        materialDetailsList: updated,
        cmoMaterialDetailsList,
        uploadedFileName,
      });
    },
    [materialDetailsList, stepData, cmoMaterialDetailsList, uploadedFileName, updateStepData]
  );

  // Handle add CMO material row (additional CMO-only row, not linked to sponsor)
  const handleAddCmoMaterialRow = useCallback(() => {
    const newMaterialId = generateMaterialId();
    const newRow = {
      _materialId: newMaterialId,
      id: newMaterialId,
      cmo_product_code: "",
      bo_material_code: "",
      material_description: "",
      cmo_material_code: "",
      cmo_material_description: "",
      cmo_material_UOM: "",
    };
    const updatedList = [...cmoMaterialDetailsList, newRow];
    setCmoMaterialDetailsList(updatedList);
    updateStepData("productDetails", {
      ...stepData,
      materialDetailsList,
      cmoMaterialDetailsList: updatedList,
      uploadedFileName,
    });
  }, [cmoMaterialDetailsList, stepData, materialDetailsList, uploadedFileName, updateStepData]);

  // Handle delete CMO material row
  const handleDeleteCmoMaterialRow = useCallback(
    (index) => {
      const updated = cmoMaterialDetailsList.filter((_, i) => i !== index);
      setCmoMaterialDetailsList(updated);
      updateStepData("productDetails", {
        ...stepData,
        materialDetailsList,
        cmoMaterialDetailsList: updated,
        uploadedFileName,
      });
    },
    [cmoMaterialDetailsList, stepData, materialDetailsList, uploadedFileName, updateStepData]
  );

  // Opens the dialog for adding a new brand
  const handleOpenBrandDialog = (event) => {
    event.preventDefault();
    setOpenBrandDialog(true);
  };

  // Closes the brand dialog and resets its state
  const handleCloseBrandDialog = () => {
    setOpenBrandDialog(false);
    setNewBrand("");
    setBrandError("");
  };

  // Adds a new brand to the brands list and selects it
  const handleAddNewBrand = () => {
    // Validate that brand name is not empty
    if (!newBrand.trim()) {
      setBrandError("Brand name cannot be empty");
      return;
    }

    // Validate brand name format (Alphabets, digits, space, Hyphens (-), Ampersand (&), Underscore (_), Apostrophe ('), Period (.))
    const brandNameRegex = /^[A-Za-z0-9 \-&_'\.]+$/;
    if (!brandNameRegex.test(newBrand.trim())) {
      setBrandError(
        "Brand name can only contain alphabets, digits, spaces, hyphens (-), ampersands (&), underscores (_), apostrophes ('), and periods (.)"
      );
      return;
    }

    // Check if this brand already exists in the list to avoid duplicates
    let brandExists = false;
    if (Array.isArray(brands)) {
      for (const brand of brands) {
        if (typeof brand === "object" && brand !== null && brand.name) {
          if (brand.name.toLowerCase() === newBrand.trim().toLowerCase()) {
            brandExists = true;
            break;
          }
        } else if (typeof brand === "string") {
          if (brand.toLowerCase() === newBrand.trim().toLowerCase()) {
            brandExists = true;
            break;
          }
        }
      }
    }

    if (brandExists) {
      setBrandError("This brand already exists in the list");
      return;
    }

    try {
      // Update the brands list
      const newBrandName = newBrand.trim();
      const updatedBrands = Array.isArray(brands) ? [...brands, newBrandName] : [newBrandName];
      setBrands(updatedBrands);

      // Set the new brand as the selected value
      handleFieldChange("brand", newBrandName);
      
      // Close the dialog
      handleCloseBrandDialog();
    } catch (error) {
      setBrandError("Failed to add new brand. Please try again.");
    }
  };

  // Handle save and next
  const handleSaveAndNext = useCallback(async () => {
    setIsSaving(true);
    setApiError(null);
    const newErrors = {};

    // Validation: Check required fields
    if (!stepData.projectID?.trim()) {
      newErrors.projectID = "Project ID is required";
    }

    if (!stepData.cmoSite?.trim()) {
      newErrors.cmoSite = "CMO - Site is required";
    }

    if (!stepData.deliverableType || (Array.isArray(stepData.deliverableType) && stepData.deliverableType.length === 0)) {
      newErrors.deliverableType = "At least one Deliverable Type must be selected";
    }

    if (!stepData.projectStartDate) {
      newErrors.projectStartDate = "Project Start Date is required";
    }

    if (!stepData.projectEndDate) {
      newErrors.projectEndDate = "Project End Date is required";
    }

    // If there are validation errors, set them and stop
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSaving(false);
      return;
    }

    setErrors({});

    try {
      // Get IDs for API (may be null/undefined if not available)
      const collabrixProductID = stepData.collabrixProductID || null;
      const collabrixProjectID = getCollabrixProjectID(); // null for new projects
      const sponsorOrgID = getSponsorOrgID();
      const cdmoOrgID = getCdmoOrgID();
      
      console.log("🔍 ProductDetails - Retrieved IDs before API call:", {
        collabrixProductID,
        collabrixProjectID,
        sponsorOrgID,
        cdmoOrgID
      });

      // Convert deliverableType array to deliverableTypeIDs (comma-separated string) - NOTE: plural as per API spec
      const deliverableTypeIDs = formatDeliverableTypeToIDs(stepData.deliverableType);
      
      // Build payload for updateProjectDetails API - exactly as per API specification
      const payload = {
        collabrixProductID: collabrixProductID,
        collabrixProjectID: collabrixProjectID || null, // null for new projects
        sponsorOrgID: sponsorOrgID,
        cdmoOrgList: [
          {
            cdmoOrgID: cdmoOrgID
          }
        ],
        projectStartDate: stepData.projectStartDate 
          ? (stepData.projectStartDate.format 
              ? stepData.projectStartDate.toISOString() 
              : (stepData.projectStartDate instanceof Date 
                  ? stepData.projectStartDate.toISOString() 
                  : stepData.projectStartDate))
          : null,
        projectEndDate: stepData.projectEndDate 
          ? (stepData.projectEndDate.format 
              ? stepData.projectEndDate.toISOString() 
              : (stepData.projectEndDate instanceof Date 
                  ? stepData.projectEndDate.toISOString() 
                  : stepData.projectEndDate))
          : null,
        deliverableTypeIDs: deliverableTypeIDs, // Comma-separated string like "1,2,3"
        projectName: stepData.projectID || "",
        projectStatus: "", // Empty as per requirement
        projectStatusID: null,
      };

      console.log("=== updateProjectDetails API Payload ===");
      console.log(JSON.stringify(payload, null, 2));
      console.log("=== END PAYLOAD ===");

      // Call API
      const response = await updateProjectDetailsAPI(payload);
      
      if (response && response.status) {
        console.log("✅ updateProjectDetails API Response:", response);
        
        // Store collabrixProjectID from API response (for new projects, this will be returned in response)
        // This collabrixProjectID will be used in subsequent steps (StatusSelection, DocumentDeliverables, AddCampaignID)
        let updatedStepData = { ...stepData };
        if (response.data?.collabrixProjectID) {
          updatedStepData.collabrixProjectID = response.data.collabrixProjectID;
          console.log("📝 Stored collabrixProjectID from API response:", response.data.collabrixProjectID);
        }
        
        // Also store IDs for future steps
        // Ensure cdmoOrgID is stored (use from payload if available, otherwise from getCdmoOrgID)
        const finalCdmoOrgID = cdmoOrgID || (payload.cdmoOrgList?.[0]?.cdmoOrgID) || null;
        updatedStepData.sponsorOrgID = sponsorOrgID;
        updatedStepData.cdmoOrgID = finalCdmoOrgID;
        updatedStepData.deliverableTypeIDs = deliverableTypeIDs;
        
        console.log("💾 Storing IDs for future steps:", {
          collabrixProjectID: updatedStepData.collabrixProjectID,
          cdmoOrgID: updatedStepData.cdmoOrgID,
          sponsorOrgID: updatedStepData.sponsorOrgID,
          payloadCdmoOrgID: payload.cdmoOrgList?.[0]?.cdmoOrgID,
          getCdmoOrgIDResult: cdmoOrgID
        });
        
        // Save to session storage - ensure data is saved immediately
        if (typeof updateStepData === "function") {
          updateStepData("productDetails", updatedStepData);
        }
        
        // Verify what was stored
        setTimeout(() => {
          try {
            const savedFormData = sessionStorage.getItem("productDevelopmentFormData");
            if (savedFormData) {
              const parsed = JSON.parse(savedFormData);
              console.log("✅ Verified stored data:", {
                cdmoOrgID: parsed.productDetails?.cdmoOrgID,
                collabrixProjectID: parsed.productDetails?.collabrixProjectID
              });
            }
          } catch (e) {
            console.error("Error verifying stored data:", e);
          }
        }, 600); // Wait for debounced save to complete

        // Navigate on success
        if (markApiCallSuccessful) {
          markApiCallSuccessful(true, null, "Product Details");
        } else {
          handleNext();
        }
      } else {
        throw new Error(response?.message || "Failed to update project details");
      }
    } catch (error) {
      console.error("Error saving product details:", error);
      // Show error message and do NOT navigate
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to save product details. Please try again.";
      setApiError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [stepData, updateStepData, formatDeliverableTypeToIDs, getCollabrixProjectID, getSponsorOrgID, getCdmoOrgID, markApiCallSuccessful, handleNext]);

  // Handle navigation to Add New Product
  const handleAddNewProduct = () => {
    // Include cdmoOrgID so the restore-by-ID effect can re-match the product on return
    // (it would otherwise only be set after handleSave succeeds).
    const resolvedCdmoOrgID = getCdmoOrgID() || stepData.cdmoOrgID;
    const dataToPersist = {
      ...stepData,
      ...(resolvedCdmoOrgID ? { cdmoOrgID: resolvedCdmoOrgID } : {}),
    };

    if (updateStepData) {
      updateStepData("productDetails", dataToPersist);
    }

    // updateStepData persists via a 500ms debounced sessionStorage write; history.push
    // navigates synchronously and unmounts this component, which clears the pending
    // timeout — so the user's latest selections would never reach storage. Write
    // synchronously here to guarantee the snapshot is intact before navigation.
    try {
      const FORM_DATA_KEY = "productDevelopmentFormData";
      const existing = JSON.parse(sessionStorage.getItem(FORM_DATA_KEY) || "{}");
      sessionStorage.setItem(
        FORM_DATA_KEY,
        JSON.stringify({ ...existing, productDetails: dataToPersist })
      );
    } catch (e) {
      console.error("Failed to persist productDetails before navigation:", e);
    }

    // Navigate to Clinical Supply Product Development stepper (3 steps) with return path
    // Manufacturing Supply Type is always "Product Development" for this flow
    history.push(
      `/pcmp/Pharma Co/${roleName}/${organizationName}/${ecosystemId}/productDevelopment/addProduct`,
      {
        fromProjectStepper: true,
        returnPath: `/pcmp/Pharma Co/${roleName}/${organizationName}/${ecosystemId}/productdevelopment/add-new-project`,
        selectedSupplytype: { supplyTypeName: "Product Development" },
      }
    );
  };

  return (
    <Box>
      {/* API Error Message */}
      {apiError && (
        <Box
          style={{
            marginBottom: "24px",
            padding: "12px 16px",
            backgroundColor: "#ffebee",
            border: "1px solid #f44336",
            borderRadius: "4px",
          }}
        >
          <Typography style={{ color: "#d32f2f", fontSize: "14px", fontWeight: 500 }}>
            {apiError}
          </Typography>
        </Box>
      )}

      {/* Product Details Section */}
      <Typography
        variant="h6"
        className={parentClasses?.formTitle}
        style={{ marginBottom: "40px" }}
      >
        Product Details
      </Typography>

      {/* Row 1: CMO Site (left) + Product Code / Description (right) */}
      <Grid className="two-col-forms-grid" container spacing={3}>
        {/* CMO - Site */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth style={{ marginBottom: "16px" }}>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              CMO - Site {!readOnly && <span className={classes.requiredStar}>*</span>}
            </Typography>
            {readOnly ? (
              <Typography style={{ marginTop: "12px", padding: "8px 0", fontSize: "16px", fontWeight: 400 }}>
                {stepData.cmoSite || "N/A"}
              </Typography>
            ) : (
              <Autocomplete
                options={cmoSites}
                getOptionLabel={(option) => {
                  if (!option) return "";
                  const vendorId = option?.vendorMosymphonyID;
                  const siteName =
                    option?.siteNameFromReport && option.siteNameFromReport.trim() !== ""
                      ? option.siteNameFromReport
                      : vendorId !== undefined && vendorId !== null
                      ? String(vendorId)
                      : "";
                  return siteName || "";
                }}
                loading={isLoading.cmoSites}
                selectOnFocus={false}
                value={
                  cmoSites.find(
                    (site) =>
                      String(site?.vendorMosymphonyID) === String(selectedCmoVendorId)
                  ) || null
                }
                onChange={(event, newValue) => {
                  if (!newValue) {
                    setSelectedCmoVendorId(null);
                    handleFieldChange("cmoSite", "");
                    setCmoProducts([]);
                    return;
                  }
                  const vendorId = String(newValue.vendorMosymphonyID);
                  setSelectedCmoVendorId(vendorId);
                  const siteName =
                    newValue?.siteNameFromReport && newValue.siteNameFromReport.trim() !== ""
                      ? newValue.siteNameFromReport
                      : vendorId;
                  handleFieldChange("cmoSite", siteName);
                  fetchProductsForCmo(vendorId);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select CMO Site"
                    style={{ marginTop: "4px" }}
                    InputProps={{
                      ...params.InputProps,
                      style: { height: "40px", padding: "0 8px" },
                    }}
                  />
                )}
              />
            )}
          </FormControl>
        </Grid>

        {/* Product Code / Description */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth style={{ marginBottom: "16px" }}>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              Product Code / Description {!readOnly && <span className={classes.requiredStar}>*</span>}
            </Typography>
            {readOnly ? (
              <Typography style={{ marginTop: "12px", padding: "8px 0", fontSize: "16px", fontWeight: 400 }}>
                {stepData.selectedProduct || "N/A"}
              </Typography>
            ) : (
              <>
                <Autocomplete
                  options={cmoProducts}
                  getOptionLabel={(product) => getProductLabel(product)}
                  loading={isLoading.cmoProducts}
                  disabled={!stepData.cmoSite || isLoading.cmoProducts}
                  selectOnFocus={false}
                  noOptionsText="No products for selected CMO"
                  value={
                    cmoProducts.find(
                      (product) => getProductLabel(product) === (stepData.selectedProduct || "")
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    if (!newValue) {
                      // Clear selection
                      handleFieldChange("selectedProduct", "");
                      return;
                    }
                    const label = getProductLabel(newValue);
                    handleFieldChange("selectedProduct", label);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      error={!!errors.selectedProduct}
                      // Always show the same placeholder; disabled state makes it grey until CMO is selected
                      placeholder="Select Product"
                      style={{ marginTop: "4px" }}
                      InputProps={{
                        ...params.InputProps,
                        style: { height: "40px", padding: "0 8px" },
                      }}
                    />
                  )}
                />
                {errors.selectedProduct && (
                  <Typography variant="caption" color="error" style={{ marginTop: "4px", display: "block" }}>
                    {errors.selectedProduct}
                  </Typography>
                )}
                <Box style={{ marginTop: "24px" }}>
                  <Typography variant="body2" style={{ display: "inline-block", marginRight: "4px" }}>
                    Need to add a new product?
                  </Typography>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={handleAddNewProduct}
                    style={{ 
                      textTransform: "none", 
                      padding: 0, 
                      minWidth: "auto",
                      textDecoration: "underline"
                    }}
                  >
                    Click here
                  </Button>
                </Box>
              </>
            )}
          </FormControl>
        </Grid>
      </Grid>

      {/* Row 2: read-only attributes like in screenshot */}
      <Grid
        className="two-col-forms-grid"
        container
        spacing={3}
        style={{ marginTop: "24px", marginBottom: "32px" }}
      >
        <Grid item xs={12} sm={6} style={{ marginBottom: "24px" }}>
          <Typography component="div" style={{ fontWeight: 700, marginBottom: "4px" }}>
            Product Modality
          </Typography>
          <Typography style={{ fontSize: "16px", fontWeight: 400 }}>
            {stepData.productModality || "NA"}
          </Typography>

          <Box style={{ marginTop: "24px" }}>
            <Typography component="div" style={{ fontWeight: 700, marginBottom: "4px" }}>
              Manufacturing Facility Type
            </Typography>
            <Typography style={{ fontSize: "16px", fontWeight: 400 }}>
              {stepData.manufacturingFacilityType || "NA"}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} style={{ marginBottom: "24px" }}>
          <Typography component="div" style={{ fontWeight: 700, marginBottom: "4px" }}>
            Product Type
          </Typography>
          <Typography style={{ fontSize: "16px", fontWeight: 400 }}>
            {stepData.productTypeName || "NA"}
          </Typography>

          <Box style={{ marginTop: "24px" }}>
            <Typography component="div" style={{ fontWeight: 700, marginBottom: "4px" }}>
              Manufacturing Supply Type
            </Typography>
            <Typography style={{ fontSize: "16px", fontWeight: 400 }}>
              {stepData.manufacturingSupplyType || "NA"}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Divider between sections */}
      <Divider style={{ margin: "32px 0" }} />

      {/* Project Details Section */}
      <Typography 
        variant="h6" 
        className={parentClasses?.formTitle}
      >
        Project Details
      </Typography>

      {/* Project Details Form - Labels on top, inputs below */}
      <Grid className="two-col-forms-grid" container spacing={3} style={{ marginTop: "24px" }}>
        {/* Project ID */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth style={{ marginBottom: "32px" }}>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              Project ID {!readOnly && <span className={classes.requiredStar}>*</span>}
            </Typography>
            {readOnly ? (
              <Typography style={{ marginTop: "12px", padding: "8px 0", fontSize: "16px", fontWeight: 400 }}>
                {stepData.projectID || "N/A"}
              </Typography>
            ) : (
              <TextField
                fullWidth
                value={stepData.projectID || ""}
                onChange={(e) => handleFieldChange("projectID", e.target.value)}
                error={!!errors.projectID}
                helperText={errors.projectID}
                variant="outlined"
                size="small"
                style={{ marginTop: "4px" }}
              />
            )}
          </FormControl>
        </Grid>

        {/* CMO - Site */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth style={{ marginBottom: "32px" }}>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              CMO - Site {!readOnly && <span className={classes.requiredStar}>*</span>}
            </Typography>
            {readOnly ? (
              <Typography style={{ marginTop: "12px", padding: "8px 0", fontSize: "16px", fontWeight: 400 }}>
                {stepData.cmoSite || "N/A"}
              </Typography>
            ) : (
              <TextField
                fullWidth
                value={stepData.cmoSite || ""}
                onChange={(e) => handleFieldChange("cmoSite", e.target.value)}
                error={!!errors.cmoSite}
                helperText={errors.cmoSite}
                variant="outlined"
                size="small"
                style={{ marginTop: "4px" }}
              />
            )}
          </FormControl>
        </Grid>

        {/* Deliverable Type — hidden on the Edit Project / Edit Campaign screens */}
        {!isEditMode && (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth style={{ marginBottom: "32px" }}>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              Deliverable Type {!readOnly && <span className={classes.requiredStar}>*</span>}
            </Typography>
            {readOnly ? (
              <Typography style={{ marginTop: "12px", padding: "8px 0", fontSize: "16px", fontWeight: 400 }}>
                {Array.isArray(stepData.deliverableType) 
                  ? stepData.deliverableType.join(", ") || "N/A"
                  : stepData.deliverableType || "N/A"}
              </Typography>
            ) : (
              <>
                {/* Display selected deliverable types as chips */}
                {stepData.deliverableType && stepData.deliverableType.length > 0 && (
                  <Box style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px", marginTop: "4px" }}>
                    {stepData.deliverableType.map((type, index) => (
                      <Chip
                        key={index}
                        label={<span style={{ fontSize: "14px", fontWeight: 500 }}>{type}</span>}
                        onDelete={() => {
                          const newTypes = stepData.deliverableType.filter((_, i) => i !== index);
                          handleFieldChange("deliverableType", newTypes);
                        }}
                        style={{ 
                          backgroundColor: "#f0f0f0",
                          height: "32px",
                        }}
                        size="medium"
                      />
                    ))}
                  </Box>
                )}
                <Select
                  fullWidth
                  value=""
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    if (selectedValue) {
                      const currentTypes = Array.isArray(stepData.deliverableType) ? stepData.deliverableType : (stepData.deliverableType ? [stepData.deliverableType] : []);
                      if (!currentTypes.includes(selectedValue)) {
                        handleFieldChange("deliverableType", [...currentTypes, selectedValue]);
                      }
                    }
                  }}
                  error={!!errors.deliverableType}
                  variant="outlined"
                  size="small"
                  style={{ marginTop: "4px", height: "40px" }}
                  displayEmpty
                  inputProps={{
                    style: { height: "40px", padding: "8px 32px 8px 14px" }
                  }}
                  disabled={isLoading.deliverableTypes}
                >
                  <MenuItem value="" disabled>Select from the list</MenuItem>
                  {isLoading.deliverableTypes ? (
                    <MenuItem value="" disabled>Loading deliverable types...</MenuItem>
                  ) : deliverableTypes.length > 0 ? (
                    deliverableTypes.map((type) => (
                      <MenuItem key={type.deliverableTypeID} value={type.deliverableTypeName}>
                        {type.deliverableTypeName}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>No deliverable types available</MenuItem>
                  )}
                </Select>
                {errors.deliverableType && (
                  <Typography variant="caption" color="error" style={{ marginTop: "4px", display: "block" }}>
                    {errors.deliverableType}
                  </Typography>
                )}
              </>
            )}
          </FormControl>
        </Grid>
        )}

        {/* Project Start Date */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth style={{ marginBottom: "32px" }}>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              Project Start Date {!readOnly && <span className={classes.requiredStar}>*</span>}
            </Typography>
            {readOnly ? (
              <Typography style={{ marginTop: "12px", padding: "8px 0", fontSize: "16px", fontWeight: 400 }}>
                {stepData.projectStartDate ? (stepData.projectStartDate.format ? stepData.projectStartDate.format(localStorage.selectedDateFormat || "DD-MMM-YYYY") : stepData.projectStartDate) : "N/A"}
              </Typography>
            ) : (
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <DatePicker
                  fullWidth
                  variant="inline"
                  inputVariant="outlined"
                  format={localStorage.selectedDateFormat ? localStorage.selectedDateFormat : "DD-MMM-YYYY"}
                  name="projectStartDate"
                  value={stepData.projectStartDate || null}
                  onChange={(date) => handleFieldChange("projectStartDate", date)}
                  error={!!errors.projectStartDate}
                  helperText={errors.projectStartDate}
                  placeholder="Select date"
                  size="small"
                  autoOk={true}
                  views={['date', 'month', 'year']}
                  openTo="date"
                  style={{ marginTop: "4px" }}
                  InputProps={{
                    endAdornment: (
                      <EventTwoToneIcon
                        style={{ color: '#0000008a', cursor: 'pointer' }}
                      />
                    ),
                  }}
                />
              </MuiPickersUtilsProvider>
            )}
          </FormControl>
        </Grid>

        {/* Project End Date */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth style={{ marginBottom: "32px" }}>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              Project End Date {!readOnly && <span className={classes.requiredStar}>*</span>}
            </Typography>
            {readOnly ? (
              <Typography style={{ marginTop: "12px", padding: "8px 0", fontSize: "16px", fontWeight: 400 }}>
                {stepData.projectEndDate ? (stepData.projectEndDate.format ? stepData.projectEndDate.format(localStorage.selectedDateFormat || "DD-MMM-YYYY") : stepData.projectEndDate) : "N/A"}
              </Typography>
            ) : (
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <DatePicker
                  fullWidth
                  variant="inline"
                  inputVariant="outlined"
                  format={localStorage.selectedDateFormat ? localStorage.selectedDateFormat : "DD-MMM-YYYY"}
                  name="projectEndDate"
                  value={stepData.projectEndDate || null}
                  onChange={(date) => handleFieldChange("projectEndDate", date)}
                  error={!!errors.projectEndDate}
                  helperText={errors.projectEndDate}
                  placeholder="Select date"
                  size="small"
                  autoOk={true}
                  views={['date', 'month', 'year']}
                  openTo="date"
                  minDate={stepData.projectStartDate || undefined}
                  disabled={!stepData.projectStartDate}
                  style={{ marginTop: "4px" }}
                  InputProps={{
                    endAdornment: (
                      <EventTwoToneIcon
                        style={{ color: '#0000008a', cursor: 'pointer' }}
                      />
                    ),
                  }}
                />
              </MuiPickersUtilsProvider>
            )}
          </FormControl>
        </Grid>
      </Grid>

      {/* Commented out - All other fields removed, only showing fields from photo */}
      {/* All remaining fields below are commented out - not in the process */}
      {false && (
        <>
        <Grid className="two-col-forms-grid" container spacing={3}>
        {/* Row 3: Product Modality, Product Type */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              Product Modality
            </Typography>
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: "4px" }}>
              <Select
                value={stepData.productModality || ""}
                onChange={(e) =>
                  handleFieldChange("productModality", e.target.value)
                }
                variant="outlined"
                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
              >
                {isLoading.productModalities ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (productModalities.length > 0
                  ? productModalities.map((modality) => (
                      <MenuItem
                        key={modality.drugTypeID}
                        value={modality.drugTypeName}
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {modality.drugTypeName}
                      </MenuItem>
                    ))
                  : PRODUCT_MODALITIES.map((modality) => (
                      <MenuItem key={modality} value={modality} style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                        {modality}
                      </MenuItem>
                    )))}
              </Select>
            </FormControl>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              Product Type
            </Typography>
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: "4px" }}>
              <Select
                value={stepData.productTypeName || stepData.productType || ""}
                onChange={(e) => handleFieldChange("productTypeName", e.target.value)}
                variant="outlined"
                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
              >
                {isLoading.productTypes ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (productTypes.length > 0
                  ? productTypes.map((type) => (
                      <MenuItem
                        key={type.productTypeID}
                        value={type.productTypeName}
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {type.productTypeName}
                      </MenuItem>
                    ))
                  : PRODUCT_TYPES.map((type) => (
                      <MenuItem key={type} value={type} style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                        {type}
                      </MenuItem>
                    )))}
              </Select>
            </FormControl>
          </FormControl>
        </Grid>

        {/* Row 4: Facility Type, Supply Type */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              Manufacturing Facility Type
            </Typography>
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: "4px" }}>
              <Select
                value={stepData.manufacturingFacilityType || ""}
                onChange={(e) =>
                  handleFieldChange("manufacturingFacilityType", e.target.value)
                }
                variant="outlined"
                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
              >
                <MenuItem value="" style={{ whiteSpace: "normal", wordWrap: "break-word" }}>Select Facility Type</MenuItem>
                {isLoading.facilityTypes ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (facilityTypes.length > 0
                  ? facilityTypes.map((type) => (
                      <MenuItem
                        key={type.facilityTypeID}
                        value={type.facilityTypeName}
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {type.facilityTypeName}
                      </MenuItem>
                    ))
                  : MANUFACTURING_FACILITY_TYPES.map((type) => (
                      <MenuItem key={type} value={type} style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                        {type}
                      </MenuItem>
                    )))}
              </Select>
            </FormControl>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              Manufacturing Supply Type
            </Typography>
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: "4px" }}>
              <Select
                value={stepData.manufacturingSupplyType || ""}
                onChange={(e) =>
                  handleFieldChange("manufacturingSupplyType", e.target.value)
                }
                variant="outlined"
                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
              >
                <MenuItem value="" style={{ whiteSpace: "normal", wordWrap: "break-word" }}>Select Supply Type</MenuItem>
                {MANUFACTURING_SUPPLY_TYPES.map((type) => (
                  <MenuItem key={type} value={type} style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormControl>
        </Grid>

        {/* Row 5: CMO Name, CMO Product Code */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              CMO Name *
            </Typography>
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: "4px" }}>
              <Select
                value={stepData.cmoName || ""}
                onChange={(e) => handleFieldChange("cmoName", e.target.value)}
                variant="outlined"
                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
              >
                <MenuItem value="Sai Life Sciences" style={{ whiteSpace: "normal", wordWrap: "break-word" }}>Sai Life Sciences</MenuItem>
              </Select>
            </FormControl>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              CMO Product Code *
            </Typography>
            <TextField
              fullWidth
              value={stepData.cmoProductCode || ""}
              onChange={(e) =>
                handleFieldChange("cmoProductCode", e.target.value)
              }
              error={!!errors.cmoProductCode}
              helperText={errors.cmoProductCode}
              variant="outlined"
              size="small"
              style={{ marginTop: "4px" }}
            />
          </FormControl>
        </Grid>

        {/* Row 6: CMO Description, Brand */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              CMO Product Description *
            </Typography>
            <TextField
              fullWidth
              value={stepData.cmoProductDescription || ""}
              onChange={(e) =>
                handleFieldChange("cmoProductDescription", e.target.value)
              }
              error={!!errors.cmoProductDescription}
              helperText={errors.cmoProductDescription}
              variant="outlined"
              size="small"
              style={{ marginTop: "4px" }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              Brand *
            </Typography>
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: "4px" }}>
              <Select
                value={stepData.brand || ""}
                onChange={(e) => handleFieldChange("brand", e.target.value)}
                error={!!errors.brand}
                variant="outlined"
                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
              >
                {brands.map((brand) => (
                  <MenuItem key={brand} value={brand} style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                    {brand}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.brand && (
              <Typography variant="caption" color="error" style={{ marginTop: "4px" }}>
                {errors.brand}
              </Typography>
            )}
            <Box mt={1}>
              <Typography
                variant="caption"
                style={{ display: "inline-block", marginRight: "4px" }}
              >
                Brand not in the list?
              </Typography>
              <Button
                onClick={handleOpenBrandDialog}
                color="primary"
                variant="text"
                size="small"
                style={{ padding: 0, minWidth: "auto", textTransform: "none", fontSize: "0.75rem" }}
              >
                Click here to add new brand
              </Button>
            </Box>
          </FormControl>
        </Grid>

        {/* Row 7: Sponsor UOM, CMO UOM */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              Sponsor UOM *
            </Typography>
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: "4px" }}>
              <Select
                value={stepData.sponsorUOM || ""}
                onChange={(e) => handleFieldChange("sponsorUOM", e.target.value)}
                error={!!errors.sponsorUOM}
                variant="outlined"
                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
              >
                {(uomOptions.length > 0 ? uomOptions : UOM_OPTIONS).map((uom) => (
                  <MenuItem key={uom} value={uom} style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                    {uom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.sponsorUOM && (
              <Typography variant="caption" color="error" style={{ marginTop: "4px" }}>
                {errors.sponsorUOM}
              </Typography>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              CMO UOM *
            </Typography>
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: "4px" }}>
              <Select
                value={stepData.cmoUOM || ""}
                onChange={(e) => handleFieldChange("cmoUOM", e.target.value)}
                error={!!errors.cmoUOM}
                variant="outlined"
                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
              >
                {(uomOptions.length > 0 ? uomOptions : UOM_OPTIONS).map((uom) => (
                  <MenuItem key={uom} value={uom} style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                    {uom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.cmoUOM && (
              <Typography variant="caption" color="error" style={{ marginTop: "4px" }}>
                {errors.cmoUOM}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/* Row 8: Status, Sponsor ERP */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              Status
            </Typography>
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: "4px" }}>
              <Select
                value={stepData.status || "Inactive"}
                onChange={(e) => handleFieldChange("status", e.target.value)}
                variant="outlined"
                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status} style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              Receiving Plant ID From Sponsor ERP
            </Typography>
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: "4px" }}>
              <Select
                value={stepData.receivingPlantID || ""}
                onChange={(e) =>
                  handleFieldChange("receivingPlantID", e.target.value)
                }
                variant="outlined"
                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
              >
                {PLANT_IDS.map((id) => (
                  <MenuItem key={id} value={id} style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                    {id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormControl>
        </Grid>

        {/* Row 9: Storage Location, QA Check */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              Receiving Storage Location IDs From Sponsor ERP
            </Typography>
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: "4px" }}>
              <Select
                multiple
                value={stepData.receivingStorageLocationIDs || []}
                onChange={(e) =>
                  handleFieldChange("receivingStorageLocationIDs", e.target.value)
                }
                renderValue={(selected) =>
                  selected.length > 1 ? "Multiple Selection" : selected.length === 1 ? selected[0] : ""
                }
                variant="outlined"
                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
              >
                {STORAGE_LOCATION_IDS.map((id) => (
                  <MenuItem key={id} value={id} style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                    <Checkbox
                      checked={
                        (stepData.receivingStorageLocationIDs || []).indexOf(id) >
                        -1
                      }
                    />
                    {id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Typography component="label" style={{ marginBottom: "4px", fontWeight: 700, wordWrap: "break-word", display: "block" }}>
              QA Check Required
            </Typography>
            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: "4px" }}>
              <Select
                value={stepData.qaCheckRequired || "YES"}
                onChange={(e) =>
                  handleFieldChange("qaCheckRequired", e.target.value)
                }
                variant="outlined"
                style={{ whiteSpace: "normal", wordWrap: "break-word" }}
              >
                <MenuItem value="YES" style={{ whiteSpace: "normal", wordWrap: "break-word" }}>YES</MenuItem>
                <MenuItem value="NO" style={{ whiteSpace: "normal", wordWrap: "break-word" }}>NO</MenuItem>
              </Select>
            </FormControl>
          </FormControl>
        </Grid>
      </Grid>
        </>
      )}
      {/* End of commented out fields - first block */}

      {/* Material Details Section - Commented out */}
      {false && (
        <>
      <Divider style={{ margin: "60px 0 24px 0" }} />
      <Typography 
        variant="h6" 
        className={classes?.formTitle}
        style={{
          marginBottom: "24px",
        }}
      >
        Material Details
      </Typography>

      {/* Input Mode Selection */}
      <Box style={{ marginBottom: "24px" }}>
        <RadioGroup
          row
          value={stepData.materialInputMode || "manual"}
          onChange={(e) =>
            handleFieldChange("materialInputMode", e.target.value)
          }
        >
          <FormControlLabel
            value="upload"
            control={<Radio style={{ color: "#ec6535" }} />}
            label="Upload Material Master Data"
          />
          <FormControlLabel
            value="manual"
            control={<Radio style={{ color: "#ec6535" }} />}
            label="Add Material Data Manually"
          />
        </RadioGroup>
      </Box>

      {/* Material Details (Sponsor) Table */}
      <Typography variant="h6" style={{ fontSize: "18px", lineHeight: "24px", fontWeight: 600, margin: "24px 0" }}>
        Material Details (Sponsor)
      </Typography>
      <TableContainer
        component={Paper}
        style={{
          border: "1px solid #E0E0E0",
          borderRadius: 8,
          marginBottom: "24px",
          overflowX: "auto",
        }}
      >
        <Table size="small" style={{ width: "100%", minWidth: "1600px" }}>
          <TableHead>
            <TableRow style={{ background: "#F4F6F8" }}>
              {[
                {
                  key: "sponsor-supplying-material",
                  content: (
                    <>
                      Sponsor supplying <br /> Material
                    </>
                  ),
                  width: "12%",
                },
                {
                  key: "material-brand",
                  content: (
                    <>
                      Material <br /> Brand
                    </>
                  ),
                  width: "6%",
                },
                {
                  key: "material-type",
                  content: "Material Type",
                  width: "12%",
                },
                {
                  key: "material-code",
                  content: (
                    <>
                      Material <br /> Code
                    </>
                  ),
                  width: "6%",
                },
                {
                  key: "material-description",
                  content: "Material Description",
                  width: "15%",
                },
                {
                  key: "material-uom",
                  content: (
                    <>
                      Material <br /> UOM
                    </>
                  ),
                  width: "8%",
                },
                {
                  key: "material-plant-id",
                  content: (
                    <>
                      Material Plant ID <br /> From Sponsor ERP
                    </>
                  ),
                  width: "15%",
                },
                {
                  key: "material-storage-location",
                  content: (
                    <>
                      Material Storage Location <br /> ID From Sponsor ERP
                    </>
                  ),
                  width: "15%",
                },
                {
                  key: "action",
                  content: "Action",
                  width: "5%",
                },
              ].map((header) => (
                <TableCell
                  key={header.key}
                  style={{
                    fontWeight: 700,
                    background: "#F4F6F8",
                    borderBottom: "2px solid #E0E0E0",
                    color: "#222",
                    fontSize: 16,
                    padding: "12px 16px",
                    width: header.width,
                  }}
                >
                  {header.content}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {materialDetailsList.map((row, index) => (
              <TableRow key={row._materialId || index}>
                <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                  <Select
                    value={row.sponsorSupplyingMaterial ? "Yes" : "No"}
                    onChange={(e) =>
                      handleMaterialDetailsChange(
                        index,
                        "sponsorSupplyingMaterial",
                        e.target.value === "Yes"
                      )
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    style={{ backgroundColor: "#fff", height: "40px" }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300
                        }
                      }
                    }}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </TableCell>
                <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                  <TextField
                    value={row.material_brand || ""}
                    onChange={(e) =>
                      handleMaterialDetailsChange(
                        index,
                        "material_brand",
                        e.target.value
                      )
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    style={{ backgroundColor: "#fff", fontSize: '14px' }}
                  />
                </TableCell>
                <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                  <TextField
                    value={row.materialTypeName || ""}
                    onChange={(e) =>
                      handleMaterialDetailsChange(
                        index,
                        "materialTypeName",
                        e.target.value
                      )
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={2}
                    placeholder="Enter Material Type"
                    style={{ backgroundColor: "#fff", fontSize: '14px' }}
                    InputProps={{
                      style: {
                        fontSize: '14px',
                        lineHeight: '1.4',
                        padding: '8px 12px'
                      }
                    }}
                  />
                </TableCell>
                <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                  <TextField
                    value={row.bo_material_code || ""}
                    onChange={(e) =>
                      handleMaterialDetailsChange(
                        index,
                        "bo_material_code",
                        e.target.value
                      )
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    style={{ backgroundColor: "#fff", fontSize: '14px' }}
                  />
                </TableCell>
                <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                  <TextField
                    value={row.material_description || ""}
                    onChange={(e) =>
                      handleMaterialDetailsChange(
                        index,
                        "material_description",
                        e.target.value
                      )
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={2}
                    placeholder="Enter Material Description"
                    style={{ backgroundColor: "#fff", fontSize: '14px' }}
                    InputProps={{
                      style: {
                        fontSize: '14px',
                        lineHeight: '1.4',
                        padding: '8px 12px'
                      }
                    }}
                  />
                </TableCell>
                <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                  <Select
                    value={row.bo_material_UOM || ""}
                    onChange={(e) =>
                      handleMaterialDetailsChange(
                        index,
                        "bo_material_UOM",
                        e.target.value
                      )
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    displayEmpty
                    style={{ backgroundColor: "#fff", fontSize: '14px' }}
                  >
                    <MenuItem value="">
                      <em>Select UOM</em>
                    </MenuItem>
                    {(uomOptions.length > 0 ? uomOptions : UOM_OPTIONS).map((uom) => (
                      <MenuItem key={uom} value={uom}>
                        {uom}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                  <Select
                    value={row.materialPlantIDFromSponsorERP || ""}
                    onChange={(e) =>
                      handleMaterialDetailsChange(
                        index,
                        "materialPlantIDFromSponsorERP",
                        e.target.value
                      )
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    displayEmpty
                    style={{ backgroundColor: "#fff", fontSize: '14px', height: "40px" }}
                    renderValue={(selected) => {
                      if (!selected || selected === "") {
                        return <span style={{ color: '#999' }}>Select</span>;
                      }
                      return selected;
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300
                        }
                      }
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select Plant ID
                    </MenuItem>
                    {vendorPlants && vendorPlants.length > 0 ? (
                      vendorPlants.map((plant, pIdx) => (
                        <MenuItem key={`plant-${pIdx}`} value={typeof plant === 'object' ? plant.plantId : plant}>
                          {typeof plant === 'object' ? plant.plantId : plant}
                        </MenuItem>
                      ))
                    ) : (
                      PLANT_IDS.map((id, pIdx) => (
                        <MenuItem key={`plant-static-${pIdx}`} value={id}>
                          {id}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </TableCell>
                <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                  <Select
                    multiple
                    value={
                      row.materialStorageLocationIDsFromSponsorERP
                        ? row.materialStorageLocationIDsFromSponsorERP.split(", ")
                        : []
                    }
                    onChange={(e) => {
                      const value = typeof e.target.value === 'string' 
                        ? e.target.value.split(',') 
                        : e.target.value;
                      handleMaterialDetailsChange(
                        index,
                        "materialStorageLocationIDsFromSponsorERP",
                        value.join(", ")
                      );
                    }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    style={{ backgroundColor: "#fff", fontSize: '14px', height: "40px" }}
                    renderValue={(selected) =>
                      selected.length > 1 ? "Multiple Selection" : selected.length === 1 ? selected[0] : ""
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300
                        }
                      }
                    }}
                  >
                    {STORAGE_LOCATION_IDS.map((id, sIdx) => (
                      <MenuItem key={`storage-static-${sIdx}`} value={id}>
                        <Checkbox
                          checked={
                            (row.materialStorageLocationIDsFromSponsorERP
                              ? row.materialStorageLocationIDsFromSponsorERP.split(", ")
                              : []
                            ).indexOf(id) > -1
                          }
                        />
                        {id}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                  <IconButton
                    onClick={() => handleDeleteMaterialRow(index)}
                    size="small"
                    style={{ color: "#ec6535" }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box style={{ marginBottom: "24px" }}>
        <Button
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddMaterialRow}
          style={{
            borderColor: "#000",
            color: "#000",
            borderRadius: "20px",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Add New Row
        </Button>
      </Box>

      {/* Material Details (CMO) Table */}
      <Typography variant="h6" style={{ fontSize: "18px", lineHeight: "24px", fontWeight: 600, margin: "24px 0" }}>
        Material Details (CMO)
      </Typography>
      <TableContainer
        component={Paper}
        style={{
          border: "1px solid #E0E0E0",
          borderRadius: 8,
          marginBottom: "24px",
        }}
      >
        <Table size="small" style={{ width: "100%" }}>
          <TableHead>
            <TableRow style={{ background: "#F4F6F8" }}>
              {[
                {
                  key: "cmo-product-code",
                  content: "CMO Product Code",
                  width: "18%",
                },
                {
                  key: "cmo-material-code",
                  content: "Sponsor Material Code",
                  width: "18%",
                },
                {
                  key: "cmo-material-code-cmo",
                  content: "CMO Material Code",
                  width: "18%",
                },
                {
                  key: "cmo-material-description-cmo",
                  content: "CMO Material Description",
                  width: "22%",
                },
                {
                  key: "cmo-material-uom",
                  content: "CMO Material UOM",
                  width: "15%",
                },
                {
                  key: "action",
                  content: "Action",
                  width: "5%",
                },
              ].map((header) => (
                <TableCell
                  key={header.key}
                  style={{
                    fontWeight: 700,
                    background: "#F4F6F8",
                    borderBottom: "2px solid #E0E0E0",
                    color: "#222",
                    fontSize: 15,
                    padding: "12px 16px",
                    width: header.width,
                  }}
                >
                  {header.content}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {cmoMaterialDetailsList.map((row, index) => {
              const isLinkedToSponsor = index < materialDetailsList.length;
              return (
                <TableRow key={row._materialId || index}>
                  <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                    <TextField
                      value={stepData.cmoProductCode || ""}
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled
                      style={{ backgroundColor: "#f5f5f5", fontSize: '14px' }}
                      InputProps={{
                        style: {
                          fontSize: '14px',
                          lineHeight: '1.4',
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                    {isLinkedToSponsor ? (
                      <TextField
                        value={row.bo_material_code || materialDetailsList[index]?.bo_material_code || ""}
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled
                        placeholder="Sponsor Material Code"
                        style={{ backgroundColor: "#f5f5f5", fontSize: '14px' }}
                        InputProps={{
                          style: {
                            fontSize: '14px',
                            lineHeight: '1.4',
                          }
                        }}
                      />
                    ) : (
                      <Select
                        value={row.bo_material_code || ""}
                        onChange={(e) => handleCmoMaterialDetailsChange(index, 'bo_material_code', e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        displayEmpty
                        style={{ backgroundColor: "#fff", fontSize: '14px' }}
                        renderValue={(selected) => {
                          if (!selected || selected === "") {
                            return <span style={{ color: '#999' }}>Select Material Code</span>;
                          }
                          return selected;
                        }}
                      >
                        <MenuItem value="" disabled>
                          Select Material Code
                        </MenuItem>
                        {materialDetailsList
                          .filter(m => m.bo_material_code && m.bo_material_code.trim())
                          .map((material, mIdx) => (
                            <MenuItem key={`material-opt-${mIdx}`} value={material.bo_material_code}>
                              {material.bo_material_code}
                            </MenuItem>
                          ))}
                      </Select>
                    )}
                  </TableCell>
                  <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                    <TextField
                      value={row.cmo_material_code || ""}
                      onChange={(e) => handleCmoMaterialDetailsChange(index, 'cmo_material_code', e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="Enter CMO Material Code"
                      style={{ backgroundColor: "#fff", fontSize: '14px' }}
                      InputProps={{
                        style: {
                          fontSize: '14px',
                          lineHeight: '1.4',
                          padding: '8px 12px'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                    <TextField
                      value={row.cmo_material_description || ""}
                      onChange={(e) => handleCmoMaterialDetailsChange(index, 'cmo_material_description', e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={2}
                      placeholder="Enter CMO Material Description"
                      style={{ backgroundColor: "#fff", fontSize: '14px' }}
                      InputProps={{
                        style: {
                          fontSize: '14px',
                          lineHeight: '1.4',
                          padding: '8px 12px'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                    <Select
                      value={row.cmo_material_UOM || ""}
                      onChange={(e) => handleCmoMaterialDetailsChange(index, 'cmo_material_UOM', e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                      displayEmpty
                      style={{ backgroundColor: "#fff", fontSize: '14px' }}
                    >
                      <MenuItem value="">
                        <em>Select UOM</em>
                      </MenuItem>
                      {(uomOptions.length > 0 ? uomOptions : UOM_OPTIONS).map((uom) => (
                        <MenuItem key={uom} value={uom}>
                          {uom}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell style={{ verticalAlign: "top", padding: "8px" }}>
                    {!isLinkedToSponsor && (
                      <IconButton
                        onClick={() => handleDeleteCmoMaterialRow(index)}
                        size="small"
                        style={{ color: "#ec6535" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box style={{ marginBottom: "24px" }}>
        <Button
          variant="outlined"
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddCmoMaterialRow}
          style={{
            borderColor: "#000",
            color: "#000",
            borderRadius: "20px",
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Add New Row
        </Button>
      </Box>

      {/* Add Brand Dialog */}
      <Dialog
        className="add-new-brand-popup"
        open={openBrandDialog}
        onClose={handleCloseBrandDialog}
      >
        <DialogTitle id="brand-dialog-title">Add New Brand</DialogTitle>
        <DialogContent>
          <Typography component="label" htmlFor="newBrandInput" style={{ marginBottom: "8px", display: "block" }}>
            Brand <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            id="newBrandInput"
            value={newBrand}
            onChange={(e) => {
              const value = e.target.value;
              setNewBrand(value);

              // Validate brand name format as user types
              const brandNameRegex = /^[A-Za-z0-9 \-&_'\.]+$/;

              if (!value.trim()) {
                setBrandError("Brand name cannot be empty");
              } else if (!brandNameRegex.test(value)) {
                setBrandError(
                  "Brand name can only contain alphabets, digits, spaces, hyphens (-), ampersands (&), underscores (_), apostrophes ('), and periods (.)"
                );
              } else {
                setBrandError("");
              }
            }}
            variant="outlined"
            fullWidth
            autoFocus
            error={Boolean(brandError)}
            helperText={brandError}
            style={{ marginTop: "4px" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBrandDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleAddNewBrand}
            color="primary"
            disabled={!newBrand.trim() || Boolean(brandError)}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

        </>
      )}
      {/* End of commented out fields */}

      {/* Upload Material Master Data Section - Commented out */}
      {false && (
        <>
          <Typography variant="h6" style={{ fontSize: "18px", lineHeight: "24px", fontWeight: 600, margin: "40px 0 12px 0" }}>
            Upload Material Master Data
          </Typography>
      <Box
        style={{
          border: "2px dashed #c6c6c6",
          borderRadius: "8px",
          padding: "20px 20px",
          textAlign: "center",
          backgroundColor: "#fafafa",
          marginBottom: "24px",
          cursor: "pointer",
        }}
        onClick={() => document.getElementById("material-upload-input")?.click()}
      >
        <CloudUploadIcon style={{ fontSize: "32px", color: "#c6c6c6", marginBottom: "8px" }} />
        <Typography variant="body1" style={{ color: "#666", fontSize: "14px" }}>
          Drag & drop file or click here to upload files
        </Typography>
        <input
          id="material-upload-input"
          type="file"
          style={{ display: "none" }}
          accept=".xlsx,.xls,.csv"
          onChange={handleFileUpload}
        />
      </Box>

      {/* View Custom Files */}
      {uploadedFileName && (
        <Box style={{ marginBottom: "32px" }}>
          <Typography variant="body2" style={{ marginBottom: "8px", fontWeight: 600 }}>
            View Custom Files
          </Typography>
          <Typography
            variant="body2"
            style={{ color: "#ec6535", cursor: "pointer" }}
            onClick={() => alert(`View file: ${uploadedFileName}`)}
          >
            {uploadedFileName}
          </Typography>
        </Box>
      )}
        </>
      )}
      {/* End of commented out Material Details Section */}

      {/* Navigation Buttons - Hidden when used in review page */}
      {!hideStepActions && (
        <Box
          sx={{
            display: "flex",
            justifyContent: activeStep === 0 ? "center" : "space-between",
            alignItems: "center",
            marginTop: "80px",
            paddingTop: "24px",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          {activeStep !== 0 && (
          <Button
            variant="contained"
            onClick={handleBack}
            className={parentClasses?.button}
            style={{
              backgroundColor: "#9E9E9E",
              color: "#fff",
              padding: "10px 24px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.875rem",
              borderRadius: 30,
            }}
          >
            Back
          </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveAndNext}
            disabled={isSaving}
            className={parentClasses?.button}
            style={{
              backgroundColor: "#ec6535",
              color: "#fff",
              padding: "10px 32px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              boxShadow: "0 2px 4px rgba(236, 101, 53, 0.3)",
              borderRadius: 30,
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={20} style={{ marginRight: 8, color: "#fff" }} />
                Saving...
              </>
            ) : (
              "Save & Next"
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ProductDetails;

